<script>
	import Router, { location } from "svelte-spa-router";
	import { wrap } from "svelte-spa-router/wrap";

	import Tailwindcss from "./Tailwindcss.svelte";

import {
	user,
	isMobile,
	isMobileDevice,
	isUserStayOnTab,
	communicationConfirmationModalDetails,
} from "./stores";

	import { onMount, onDestroy, setContext } from "svelte";

	// Components
	import Navbar from "./Components/Layout/Headbar/Navbar.svelte";
	import Sidebar from "./Components/Layout/Sidebar/Sidebar.svelte";

	import Error404Route from "./Routes/Errors/404.svelte";

	import {
		ErrorModal,
		PageLoading,
		PreventRedirectModal,
	} from "./Components/Common/index.svelte";

	import {
		checkTokenExpiration,
		loadCalendarEventCategories,
		showError,
		signOut,
	} from "./Utils/utilFunctions.svelte";

	import InfoModal from "./Components/Common/InfoModal.svelte";
	import PreviewImageModal from "./Components/Common/PreviewImageModal.svelte";
	import RetryApiErrorModal from "./Components/Common/RetryApiErrorModal.svelte";
	import ServerDisconnectionModal from "./Components/Common/ServerDisconnectionModal.svelte";
	import ViewEncounterSidePanel from "./Pages/Calendar/components/ViewEncounterSidePanel.svelte";
	import { cacheDefaultFilters } from "./Utils/utilCache.svelte";
	import ViewLogDetailsModal from "./Components/Common/LogDetailsModal/ViewLogDetailsModal.svelte";
import GlobalNoteModal from "./Components/Common/GlobalNoteModal.svelte";
import CommunicationConfirmationModal from "./Components/Common/CommunicationConfirmationModal.svelte";
import ConfirmActionNotificationModal from "./Components/Common/ConfirmActionNotificationModal.svelte";

	const showNavAndSidebarRouteArr = ["VERIFY", "RESETPASSWORD"];

	let sidebarExpanded = true,
		innerWidth = window.innerWidth,
		showNotificationOnModalDevice = false;

	// awaiting expiration check before redirect to dashboard
	let isCheckingTokenExpiration = true;

	const MobileBreakpointPixel = 768;

	setContext("sidebarState", {
		getSidebarExpanded: () => sidebarExpanded,
		setSidebarExpanded: () => (sidebarExpanded = !sidebarExpanded),
		expandSidebar: () => (sidebarExpanded = true),
		collapseSidebar: () => (sidebarExpanded = false),
	});

	$: if (innerWidth > MobileBreakpointPixel) $isMobile = false;
	else $isMobile = true;

	$: showNavAndSidebar = !showNavAndSidebarRouteArr.includes(
		$location.replace("/", "").toUpperCase(),
	);

	onMount(async () => {
		// avoid incognito initial refresh token and show invalid token error
		if ($user?.expiration) {
			// isCheckingTokenExpiration = true;

			const tokenRefreshResult = await checkTokenExpiration();

			isCheckingTokenExpiration = false;

			if (
				tokenRefreshResult != "valid" &&
				tokenRefreshResult != "refreshed"
			) {
				signOut({ reason: "Invalid token" }, true);
				return;
			}
		} else isCheckingTokenExpiration = false;

		if (innerWidth < MobileBreakpointPixel && sidebarExpanded) {
			sidebarExpanded = false;
		}

		$isMobileDevice =
			/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
				navigator.userAgent,
			);

		if ($isMobileDevice && !showNotificationOnModalDevice)
			_openNotificationOnModalDevice();

		document.addEventListener("visibilitychange", handleVisibilityChange);

		await cacheDefaultFilters();
	});

	onDestroy(() => {
		document.removeEventListener(
			"visibilitychange",
			handleVisibilityChange,
		);
	});

	const noAuthRoutes = {
		"/": wrap({
			asyncComponent: () => import("./Routes/Auth/SignInRoute.svelte"),
		}),
		"/sign-up": wrap({
			asyncComponent: () => import("./Routes/Auth/SignUpRoute.svelte"),
		}),
		"/forget-password": wrap({
			asyncComponent: () => import("./Routes/Auth/ForgotPwdRoute.svelte"),
		}),
		"/ResetPassword": wrap({
			asyncComponent: () => import("./Routes/Auth/ResetPwdRoute.svelte"),
		}),
		"/mfa-auth": wrap({
			asyncComponent: () => import("./Routes/Auth/MfaAuthRoute.svelte"),
		}),

		// Verify Email routes
		"/Verify": wrap({
			asyncComponent: () =>
				import("./Routes/Verify/VerifyEmailRoute.svelte"),
		}),

		// Catch all invalid routes
		"*": Error404Route,
	};

	const authRoutes = {
		"/": wrap({
			asyncComponent: () =>
				import("./Routes/Dashboard/DashboardRoute.svelte"),
		}),

		// Avoid Error page when user login via MFA
		"/mfa-auth": wrap({
			asyncComponent: () => import("./Routes/Errors/Transition.svelte"),
		}),

		// Reset password
		"/ResetPassword": wrap({
			asyncComponent: () => import("./Routes/Auth/ResetPwdRoute.svelte"),
		}),

		// Admin page routes
		"/Admin/:query?": wrap({
			asyncComponent: () => import("./Routes/Admin/AdminRoute.svelte"),
		}),
		"/Admin/view/:id": wrap({
			asyncComponent: () =>
				import("./Routes/Admin/ViewAdminRoute.svelte"),
		}),
		"/Admin/create/new": wrap({
			asyncComponent: () =>
				import("./Routes/Admin/CreateAdminRoute.svelte"),
		}),

		// Account page routes
		"/Account/:query?": wrap({
			asyncComponent: () =>
				import("./Routes/Account/AccountRoute.svelte"),
		}),
		"/Account/create/new": wrap({
			asyncComponent: () =>
				import("./Routes/Account/CreateAccountRoute.svelte"),
		}),
		"/Account/view/:id/:query?": wrap({
			asyncComponent: () =>
				import("./Routes/Account/ViewAccountRoute.svelte"),
		}),

		// Account Usage page routes
		"/AccountUsage/:query?": wrap({
			asyncComponent: () =>
				import("./Routes/AccountUsage/AccountUsageRoute.svelte"),
		}),

		// Account Invoices page routes
		"/AccountInvoice/:query?": wrap({
			asyncComponent: () =>
				import("./Routes/AccountInvoices/AccountInvoicesRoute.svelte"),
		}),
		"/AccountInvoice/view/:id": wrap({
			asyncComponent: () =>
				import(
					"./Routes/AccountInvoices/ViewAccountInvoicesRoute.svelte"
				),
		}),

		// Account Presets
		"/AccountPresets/:query?": wrap({
			asyncComponent: () =>
				import("./Routes/Presets/PresetsRoute.svelte"),
		}),

		// External Groups page routes
		"/ExternalGroups/:query?": wrap({
			asyncComponent: () =>
				import("./Routes/ExternalGroups/ExternalGroupsRoute.svelte"),
		}),
		"/ExternalGroups/view/:id": wrap({
			asyncComponent: () =>
				import(
					"./Routes/ExternalGroups/ViewExternalGroupsGroupRoute.svelte"
				),
		}),

		// Groups page routes
		"/Groups/:query?": wrap({
			asyncComponent: () => import("./Routes/Groups/GroupsRoute.svelte"),
		}),
		"/Groups/view/:id/:query?": wrap({
			asyncComponent: () =>
				import("./Routes/Groups/ViewGroupRoute.svelte"),
		}),
		"/Groups/create/new": wrap({
			asyncComponent: () =>
				import("./Routes/Groups/CreateGroupRoute.svelte"),
		}),

		// Group Invoices routes
		"/GroupInvoices/:query?": wrap({
			asyncComponent: () =>
				import("./Routes/GroupInvoices/GroupInvoicesRoute.svelte"),
		}),
		"/GroupInvoices/view/:id": wrap({
			asyncComponent: () =>
				import("./Routes/GroupInvoices/ViewGroupInvoicesRoute.svelte"),
		}),

		// Account schedules page routes
		"/AccountSchedules/:query?": wrap({
			asyncComponent: () =>
				import(
					"./Routes/AccountSchedules/AccountSchedulesRoute.svelte"
				),
		}),

		// Patient page routes
		"/Patient/:query?": wrap({
			asyncComponent: () =>
				import("./Routes/Patient/PatientRoute.svelte"),
		}),
		"/Patient/view/:id": wrap({
			asyncComponent: () =>
				import("./Routes/Patient/ViewPatientRoute.svelte"),
		}),

		// Patient Encounters page routes
		"/PatientEncounter/:query?": wrap({
			asyncComponent: () =>
				import(
					"./Routes/PatientEncounter/PatientEncounterRoute.svelte"
				),
		}),
		"/PatientEncounter/view/:id/:query?": wrap({
			asyncComponent: () =>
				import(
					"./Routes/PatientEncounter/ViewPatientEncounterRoute.svelte"
				),
		}),
		"/PatientEncounter/create/new": wrap({
			asyncComponent: () =>
				import(
					"./Routes/PatientEncounter/CreateAndEditPatientEncounterRoute.svelte"
				),
		}),
		"/PatientEncounter/create/multi": wrap({
			asyncComponent: () =>
				import(
					"./Routes/PatientEncounter/CreateMultiPatientEncounterRoute.svelte"
				),
		}),
		"/PatientEncounter/create/:calendarQuery?": wrap({
			asyncComponent: () =>
				import(
					"./Routes/PatientEncounter/CreateAndEditPatientEncounterRoute.svelte"
				),
		}),
		"/PatientEncounter/edit/:id/:query?": wrap({
			asyncComponent: () =>
				import(
					"./Routes/PatientEncounter/CreateAndEditPatientEncounterRoute.svelte"
				),
		}),

		// Patient Quotes page routes
		"/PatientQuote/:query?": wrap({
			asyncComponent: () =>
				import("./Routes/PatientQuote/PatientQuoteRoute.svelte"),
		}),
		"/PatientQuote/view/:id/:query?": wrap({
			asyncComponent: () =>
				import("./Routes/PatientQuote/ViewPatientQuoteRoute.svelte"),
		}),
		"/PatientQuote/create/new": wrap({
			asyncComponent: () =>
				import(
					"./Routes/PatientQuote/CreateAndEditPatientQuoteRoute.svelte"
				),
		}),
		"/PatientQuote/edit/:id/:query?": wrap({
			asyncComponent: () =>
				import(
					"./Routes/PatientQuote/CreateAndEditPatientQuoteRoute.svelte"
				),
		}),

		// Billing Rules
		"/BillingRules/:query?": wrap({
			asyncComponent: () =>
				import("./Routes/BillingRules/BillingRulesRoute.svelte"),
		}),

		// Estimates page routes
		"/Estimates/:query?": wrap({
			asyncComponent: () =>
				import("./Routes/Estimates/EstimatesRoute.svelte"),
		}),
		"/Estimates/view/:id": wrap({
			asyncComponent: () =>
				import("./Routes/Estimates/ViewEstimatesRoute.svelte"),
		}),

		// Invoices page routes
		"/Invoices/:query?": wrap({
			asyncComponent: () =>
				import("./Routes/Invoices/InvoicesRoute.svelte"),
		}),
		"/Invoices/view/:id": wrap({
			asyncComponent: () =>
				import("./Routes/Invoices/ViewInvoicesRoute.svelte"),
		}),

		// Receipts page routes
		"/Receipts/:query?": wrap({
			asyncComponent: () =>
				import("./Routes/Receipts/ReceiptsRoute.svelte"),
		}),

		// Reconciliation page routes
		"/Reconciliations/:query?": wrap({
			asyncComponent: () =>
				import("./Routes/Reconciliations/ReconciliationsRoute.svelte"),
		}),

		// My Invoices page routes
		"/MyInvoices/:query?": wrap({
			asyncComponent: () =>
				import("./Routes/DoctorInvoices/MyInvoicesRoute.svelte"),
		}),

		// Reports page routes
		"/Reports/:query?": wrap({
			asyncComponent: () =>
				import("./Routes/Reports/ReportsRoute.svelte"),
		}),

		// Surgery Rooms page routes
		"/SurgeryRooms/:query?": wrap({
			asyncComponent: () =>
				import("./Routes/SurgeryRooms/SurgeryRoomsRoute.svelte"),
		}),
		"/SurgeryRooms/view/:id": wrap({
			asyncComponent: () =>
				import("./Routes/SurgeryRooms/ViewSurgeryRoomRoute.svelte"),
		}),
		"/SurgeryRooms/create/new": wrap({
			asyncComponent: () =>
				import("./Routes/SurgeryRooms/CreateSurgeryRoomRoute.svelte"),
		}),

		// Providers page routes
		"/AllProviders/:query?": wrap({
			asyncComponent: () =>
				import("./Routes/AllProviders/AllProvidersRoute.svelte"),
		}),

		// MBS Items Routes
		"/MBSItems/:query?": wrap({
			asyncComponent: () =>
				import("./Routes/MBSItems/MBSItemsRoute.svelte"),
		}),
		"/MBSItems/view/:id": wrap({
			asyncComponent: () =>
				import("./Routes/MBSItems/ViewMBSItemRoute.svelte"),
		}),

		// ASA Items Routes
		"/ASAItems/:query?": wrap({
			asyncComponent: () =>
				import("./Routes/ASAItems/ASAItemsRoute.svelte"),
		}),
		"/ASAItems/view/:id": wrap({
			asyncComponent: () =>
				import("./Routes/ASAItems/ViewASAItemRoute.svelte"),
		}),

		"/CalendarCategories/:query?": wrap({
			asyncComponent: () =>
				import(
					"./Routes/CalendarCategories/CalendarCategoriesRoute.svelte"
				),
		}),

		"/GlobalEvents/:query?": wrap({
			asyncComponent: () =>
				import("./Routes/GlobalEvents/GlobalEventsRoute.svelte"),
		}),

		"/PublicHolidays/:query?": wrap({
			asyncComponent: () =>
				import("./Routes/PublicHolidays/PublicHolidaysRoute.svelte"),
		}),

		// Hospitals page routes
		"/Hospitals/:query?": wrap({
			asyncComponent: () =>
				import("./Routes/Hospitals/HospitalsRoute.svelte"),
		}),
		"/Hospitals/view/:id": wrap({
			asyncComponent: () =>
				import("./Routes/Hospitals/ViewHospitalsRoute.svelte"),
		}),

		// Insurance Funds page routes
		"/InsuranceFunds/:query?": wrap({
			asyncComponent: () =>
				import("./Routes/InsuranceFunds/InsuranceFundsRoute.svelte"),
		}),
		"/InsuranceFunds/view/:id": wrap({
			asyncComponent: () =>
				import("./Routes/InsuranceFunds/ViewInsuranceFundRoute.svelte"),
		}),

		// Bupa Practices page routes
		"/BupaPracticeIds/:query?": wrap({
			asyncComponent: () =>
				import("./Routes/BupaPracticeIds/BupaPracticeIdsRoute.svelte"),
		}),

		// Work Cover Authorities page routes
		"/WorkCoverAuthorities/:query?": wrap({
			asyncComponent: () =>
				import(
					"./Routes/WorkCoverAuthorities/WorkCoverAuthoritiesRoute.svelte"
				),
		}),

		// Traffic Accident Authorities page routes
		"/TrafficAccidentAuthorities/:query?": wrap({
			asyncComponent: () =>
				import(
					"./Routes/TrafficAccidentAuthorities/TrafficAccidentAuthoritiesRoute.svelte"
				),
		}),

		// PreOp Instructions page routes
		"/PreOpInstructions/:query?": wrap({
			asyncComponent: () =>
				import(
					"./Routes/PreOpInstructions/PreOpInstructionsRoute.svelte"
				),
		}),
		"/PreOpInstructions/create/new": wrap({
			asyncComponent: () =>
				import(
					"./Routes/PreOpInstructions/CreatePreOpInstructionRoute.svelte"
				),
		}),
		"/PreOpInstructions/view/:id/:query?": wrap({
			asyncComponent: () =>
				import(
					"./Routes/PreOpInstructions/ViewPreOpInstructionRoute.svelte"
				),
		}),

		// PreOp Questionnaire page routes
		"/PreOpQuestionnaires/:query?": wrap({
			asyncComponent: () =>
				import(
					"./Routes/PreOpQuestionnaire/PreOpQuestionnaireRoute.svelte"
				),
		}),

		// Email Templates page routes
		"/EmailTemplates/:query?": wrap({
			asyncComponent: () =>
				import("./Routes/EmailTemplates/EmailTemplatesRoute.svelte"),
		}),
		"/EmailTemplates/view/:id": wrap({
			asyncComponent: () =>
				import("./Routes/EmailTemplates/ViewEmailTemplateRoute.svelte"),
		}),

		// SMS Templates page routes
		"/SmsTemplates/:query?": wrap({
			asyncComponent: () =>
				import("./Routes/SmsTemplates/SmsTemplatesRoute.svelte"),
		}),
		"/SmsTemplates/view/:id": wrap({
			asyncComponent: () =>
				import("./Routes/SmsTemplates/ViewSmsTemplateRoute.svelte"),
		}),

		// PDF Templates page routes
		"/PdfTemplates/:query?": wrap({
			asyncComponent: () =>
				import("./Routes/PdfTemplates/PdfTemplatesRoute.svelte"),
		}),
		"/PdfTemplates/view/:id": wrap({
			asyncComponent: () =>
				import("./Routes/PdfTemplates/ViewPdfTemplateRoute.svelte"),
		}),

		// Activity logs routes
		"/ActivityLogs/:query?": wrap({
			asyncComponent: () =>
				import("./Routes/ActivityLogs/ActivityLogsRoute.svelte"),
		}),
		"/ActivityLogs/view/:id": wrap({
			asyncComponent: () =>
				import("./Routes/ActivityLogs/ViewActivityLogsRoute.svelte"),
		}),

		// Eclipse logs routes
		"/EclipseLogs/:query?": wrap({
			asyncComponent: () =>
				import("./Routes/EclipseLogs/EclipseLogsRoute.svelte"),
		}),
		"/EclipseLogs/view/:id": wrap({
			asyncComponent: () =>
				import("./Routes/EclipseLogs/ViewEclipseLogRoute.svelte"),
		}),

		// Login logs routes
		"/LoginLogs/:query?": wrap({
			asyncComponent: () =>
				import("./Routes/LoginLogs/LoginLogsRoute.svelte"),
		}),

		// Unauthorised logs routes
		"/UnauthorisedLogs/:query?": wrap({
			asyncComponent: () =>
				import(
					"./Routes/UnahthorisedLogs/UnauthorisedLogsRoute.svelte"
				),
		}),
		"/UnauthorisedLogs/view/:id": wrap({
			asyncComponent: () =>
				import(
					"./Routes/UnahthorisedLogs/ViewUnauthorisedLogRoute.svelte"
				),
		}),

		// Exception logs routes
		"/ExceptionLogs/:query?": wrap({
			asyncComponent: () =>
				import("./Routes/ExceptionLogs/ExceptionLogsRoute.svelte"),
		}),
		"/ExceptionLogs/view/:id": wrap({
			asyncComponent: () =>
				import("./Routes/ExceptionLogs/ViewExceptionLogRoute.svelte"),
		}),

		// Payments page routes
		"/Payments/eftcsv": wrap({
			asyncComponent: () =>
				import("./Routes/Payments/Payment_EFTCSV_Route.svelte"),
		}),

		// Profile page routes
		"/Profile/:query?": wrap({
			asyncComponent: () =>
				import("./Routes/Profile/ProfileRoute.svelte"),
		}),

		// Verify Email routes
		"/Verify": wrap({
			asyncComponent: () =>
				import("./Routes/Verify/VerifyEmailRoute.svelte"),
		}),

		// Announcements routes
		"/Announcements/:query?": wrap({
			asyncComponent: () =>
				import("./Routes/Announcements/AnnouncementsRoute.svelte"),
		}),
		
		"/Announcements/view/:id": wrap({
			asyncComponent: () =>
				import("./Routes/Announcements/ViewAnnouncementRoute.svelte"),
		}),
		"/Announcements/edit/:id": wrap({
			asyncComponent: () =>
				import("./Routes/Announcements/EditAnnouncementRoute.svelte"),
		}),
		// Company documents routes
		"/CompanyDocuments/:query?": wrap({
			asyncComponent: () =>
				import("./Routes/CompanyDocuments/CompanyDocumentsRoute.svelte"),
		}),
		//Order products routes
		"/OrderProducts/:query?": wrap({
			asyncComponent: () =>
				import("./Routes/OrderProducts/OrderProductsRoute.svelte"),
		}),

		// Calendar routes
		"/Calendar/:query?": wrap({
			asyncComponent: () =>
				import("./Routes/Calendar/CalendarRoute.svelte"),
		}),
		"/DailyPlanner/:query?": wrap({
			asyncComponent: () =>
				import("./Routes/Calendar/DailyPlannerRoute.svelte"),
		}),

		// Portal Customizations routes
		"/PortalCustomisation/:query?": wrap({
			asyncComponent: () =>
				import(
					"./Routes/PortalCustomisations.svelte/PortalCustomisationsRoute.svelte"
				),
		}),

		// InProgress routes
		// "/InProgress/:query?": wrap({
		// 	asyncComponent: () =>
		// 		import("./Routes/InProgress/InProgressRoute.svelte"),
		// }),

		// consentDue routes
		"/ConsentDue/:query?": wrap({
			asyncComponent: () =>
				import("./Routes/ConsentDue/ConsentDueRoute.svelte"),
		}),

		// PatientPaymentDue routes
		"/PatientPaymentDue/:query?": wrap({
			asyncComponent: () =>
				import(
					"./Routes/PatientPaymentDue/PatientPaymentDueRoute.svelte"
				),
		}),

		// UnsentDocuments routes
		"/UnsentDocuments/:query?": wrap({
			asyncComponent: () =>
				import(
					"./Routes/UnsentDocuments/UnsentDocumentsRoute.svelte"
				),
		}),

		// FollowUp routes
		"/FollowUp/:query?": wrap({
			asyncComponent: () =>
				import("./Routes/FollowUp/FollowUpRoute.svelte"),
		}),

		// ClaimError routes
		"/ClaimError/:query?": wrap({
			asyncComponent: () =>
				import("./Routes/ClaimError/ClaimErrorRoute.svelte"),
		}),

		// Underpaid routes
		"/Underpaid/:query?": wrap({
			asyncComponent: () =>
				import("./Routes/Underpaid/UnderpaidRoute.svelte"),
		}),

		// Overpaid routes
		"/Overpaid/:query?": wrap({
			asyncComponent: () =>
				import("./Routes/Overpaid/OverpaidRoute.svelte"),
		}),

		// PendingRefund routes
		"/PendingRefund/:query?": wrap({
			asyncComponent: () =>
				import("./Routes/PendingRefund/PendingRefundRoute.svelte"),
		}),

		// PendingReview routes
		"/PendingReview/:query?": wrap({
			asyncComponent: () =>
				import("./Routes/PendingReview/PendingReviewRoute.svelte"),
		}),

		// DebtCollection routes
		"/DebtCollection/:query?": wrap({
			asyncComponent: () =>
				import("./Routes/DebtCollection/DebtCollectionRoute.svelte"),
		}),

		// On-hold and Removed routes
		"/OnHoldAndRemoved/:query?": wrap({
			asyncComponent: () =>
				import(
					"./Routes/OnholdAndRemoved/OnholdAndRemovedRoute.svelte"
				),
		}),

		// Default Filters routes
		"/DefaultFilters/:query?": wrap({
			asyncComponent: () =>
				import("./Routes/DefaultFilters/DefaultFiltersRoute.svelte"),
		}),
		"/DefaultFilters/create/new": wrap({
			asyncComponent: () =>
				import(
					"./Routes/DefaultFilters/CreateDefaultFiltersRoute.svelte"
				),
		}),
		"/DefaultFilters/view/:id": wrap({
			asyncComponent: () =>
				import(
					"./Routes/DefaultFilters/EditDefaultFiltersRoute.svelte"
				),
		}),

		// Third Party Insurer routes
		"/ThirdPartyInsurer/:query?": wrap({
			asyncComponent: () =>
				import(
					"./Routes/ThirdPartyInsurer/ThirdPartyInsurerRoute.svelte"
				),
		}),

		// Catch all invalid routes
		"*": Error404Route,
	};

	const handleClickNotificationBtnOnMobileDevice = (
		isConfirmUseApp = false,
	) => {
		_closeNotificationOnModalDevice();

		if (isConfirmUseApp) {
			const userAgent = navigator.userAgent;

			if (/Android/i.test(userAgent))
				window.location.href = `https://play.google.com/store/apps/details?id=health.tci`;
			else if (/iPhone|iPad|iPod/i.test(userAgent))
				window.location.href = `https://apps.apple.com/au/app/anaemate/id6443842097`;
			else
				showError(
					"Unable to locate app from app store, please try manually search our app from your app store.",
				);
		}
	};

	const handleVisibilityChange = () => {
		$isUserStayOnTab = !document.hidden;
	};

	// HELPER FUNCTIONS
	const _openNotificationOnModalDevice = () =>
		(showNotificationOnModalDevice = true);

	const _closeNotificationOnModalDevice = () =>
		(showNotificationOnModalDevice = false);
</script>

<Tailwindcss />
<svelte:window bind:innerWidth />

{#if isCheckingTokenExpiration}
	<div class="w-screen h-screen">
		<PageLoading />
	</div>
{:else}
	<div
		class="w-screen flex flex-row text-slate-600 {$isMobile
			? 'h-screen min-h-screen'
			: 'h-screen'}"
	>
		{#if $user.token && $user.refreshToken && showNavAndSidebar}
			<div
				class="flex flex-1 flex-col bg-gradient-to-r from-primary to-secondary text-neutral"
				use:loadCalendarEventCategories
			>
				<Navbar bind:sidebarExpanded />
				<div class="main flex flex-1 relative">
					{#if !$isMobile}
						<Sidebar {sidebarExpanded} />
					{/if}
					<div
						class="flex flex-1 flex-col justify-center items-center w-full bg-white"
					>
						<Router routes={authRoutes} />
					</div>
				</div>
			</div>
		{:else if $user.token && $user.refreshToken}
			<div
				class="flex flex-col justify-center items-center w-screen h-screen mx-auto bg-white"
				use:loadCalendarEventCategories
			>
				<Router routes={authRoutes} />
			</div>
		{:else}
			<div
				class="flex flex-col justify-center items-center w-screen h-screen mx-auto bg-white"
			>
				<Router routes={noAuthRoutes} />
			</div>
		{/if}

		{#if $isMobileDevice && showNotificationOnModalDevice}
			<div
				class="toast toast-center toast-bottom w-full h-auto absolute b-0"
			>
				<div
					class="alert alert-secondary flex flex-1 w-full flex-col gap-4 shadow-lg"
				>
					<div class="flex flex-1 w-full items-center gap-x-4">
						<img
							src="/favicon.png"
							alt=""
							class="w-[48px] h-[48px]"
						/>
						<div class="flex flex-col gap-y-2">
							<p class="font-bold text-lg text-primary">
								Anaemate says
							</p>
							<p class="text-primary">
								Manage your patients and encounters with the
								Anaemate app
							</p>
						</div>
					</div>
					<div
						class="flex flex-1 w-full gap-x-4 items-center justify-end"
					>
						<button
							class="btn btn-ghost"
							on:click|preventDefault={() =>
								handleClickNotificationBtnOnMobileDevice()}
							>No Thanks</button
						>
						<button
							class="btn btn-secondary"
							on:click|preventDefault={() =>
								handleClickNotificationBtnOnMobileDevice(true)}
							>Use the App</button
						>
					</div>
				</div>
			</div>
		{/if}
	</div>
{/if}

<ErrorModal />

<RetryApiErrorModal />

<PreventRedirectModal />

<InfoModal />

<PreviewImageModal />

<ServerDisconnectionModal />

<ViewEncounterSidePanel />

<ViewLogDetailsModal />

<GlobalNoteModal />

<CommunicationConfirmationModal
	encounterDetails={$communicationConfirmationModalDetails.encounterDetails}
	onProceed={$communicationConfirmationModalDetails.onProceed}
/>

<ConfirmActionNotificationModal />

<style>
	@import url("https://fonts.cdnfonts.com/css/azo-sans");
	* {
		font-family: "Azo Sans", sans-serif;
		color: #6f726c;
	}

	:global(.z-index-4) {
		z-index: 4;
	}

	:global(.w-minus48) {
		width: calc(100vw - 48px);
	}

	:global(.arrow-up) {
		transform: rotate(90deg);
		transition: 0.2s linear;
	}

	:global(.arrow-down) {
		transform: rotate(270deg);
		transition: 0.2s linear;
	}

	:global(.sortable-header-decoration) {
		font-weight: 600;
		cursor: pointer;
		color: #5018a8;
	}

	:global(.sortable-header-decoration):hover {
		font-weight: 800;
		text-decoration: underline;
	}

	:global(.tooltip-trigger) {
		z-index: 100;
		text-overflow: ellipsis;
		white-space: nowrap;
		overflow: visible;
	}

	:global(.custom-tooltip) {
		display: none;
		position: absolute;
		background: white;
		color: black;
		box-shadow: 0 0 3px 2px #c8c8c8;
		padding: 6px 6px 6px 20px;
		font-size: 15px;
		border-radius: 0.5rem;
		overflow: hidden;
		min-width: 200px;
		transform: none;
	}

	:global(
			.tooltip-trigger:hover + .custom-tooltip,
			.tooltip-trigger:hover > .custom-tooltip
		) {
		display: block;
	}

	:global(.tooltip::before) {
		z-index: 1000;
	}

	:global(.custom-tooltipRight) {
		left: calc(100% + 5px);
		z-index: 1000;
	}

	:global(.custom-tooltipLeft) {
		right: calc(100% + 5px);
		z-index: 1000;
	}

	:global(.custom-tooltipTop) {
		bottom: calc(100% + 5px);
		z-index: 1000;
	}

	:global(.custom-tooltipBottom) {
		top: calc(100% + 5px);
		z-index: 1000;
	}

	:global(.no-transform) {
		transform: none !important;
	}

	:global(.inputFocus) {
		outline-offset: 2px;
		outline: 2px solid hsla(var(--bc) / 0.2);
	}

	:global(.borderB) {
		border-bottom: 2px solid #818e9b;
	}

	:global(.autoFillSelector) {
		--border: none;
		--height: 22px;
		--value-container-padding: 0;
	}

	:global(.modal-box) {
		border: 2px solid #8a3ed777;
		box-shadow: 0 25px 50px -12px rgba(138, 62, 215, 0.5) !important;
	}

	:global(.force-w-full) {
		width: 100% !important;
	}

	:global(.z-max) {
		z-index: 9999999 !important;
	}

	:global(.tooltip::before) {
		color: #000 !important;
		background-color: rgb(243 244 246) !important;
		border-radius: 0.5rem !important;
	}

	:global(.tooltip.tooltip-secondary::before) {
		border: 1px solid #8a3ed7 !important;
	}

	:global(.tooltip.tooltip-error::before) {
		border: 1px solid #f00 !important;
	}

	:global(.tooltip.tooltip-warning::before) {
		border: 1px solid rgb(255, 187, 0) !important;
	}

	:global(.plink) {
		text-decoration: underline;
		font-weight: 700;
		cursor: pointer;
		word-break: break-all;
	}

	:global(.plink:hover) {
		color: #8a3ed7;
	}

	:global(.toggle-secondary:disabled:checked) {
		background-color: #8a3ed7 !important;
		opacity: 0.5;
	}

	:global(.toggle-secondary:disabled) {
		cursor: default !important;
	}

	:global(.toggle-secondary:not(:checked):not(:disabled)) {
		--tw-bg-opacity: 0.5 !important;
	}

	:global(html) {
		width: 100vw;
		height: 100vh;
		display: flex;
	}

	:global(body) {
		width: 100vw;
		/*Resize for desired default zoom */
		zoom: 100%;
		flex: 1;
		display: flex;
	}

	:global(.w-screen) {
		flex: 1;
		height: 100% !important;
	}

	:global(.side-panel-container){
	}
</style>
